import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getAccessToken } from 'src/api/axiosClient/tokenStorage';
import { useSignupApi } from '../api';
import { errorMessage, isTransientFailure } from '../helpers/apiErrors';
import { getSignupToken, setSignupToken } from '../helpers/signupSession';
import { signupCodeValidationSchema } from '../helpers/validation';
import { SignupStartedResponse } from '../types';

const LINK_TOKEN_PARAM = 't';

// The plan screen, which is where the funnel decides on startup which step a signed-in client belongs on.
const SIGNUP_ROUTE = '/signup';

// The last line of defence: the screen stops offering both actions below once `sessionMissing` is true, and
// this answers a session lost between that decision and the click. Without it the client reads the API's own
// complaint about a missing header.
const NO_SESSION_MESSAGE = 'This browser has no signup in progress. Open the link from the email instead.';

export const useSignupVerifyEmailViewController = () => {
  const api = useSignupApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const linkToken = searchParams.get(LINK_TOKEN_PARAM);

  // Set by the screen that sent the client here when the email they are holding cannot work any more.
  const codeIsStale = (location.state as { signupCodeStale?: unknown } | null)?.signupCodeStale === true;

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [linkNotice, setLinkNotice] = useState<string | null>(null);
  const [confirmingLink, setConfirmingLink] = useState(linkToken != null);
  const [linkUnavailable, setLinkUnavailable] = useState(false);
  const [linkAttempt, setLinkAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  /**
   * Confirming proves the address and nothing more: no account exists yet, and the company details are what
   * raise one. The session comes back with the answer and is stored here rather than assumed — the browser
   * that opened the emailed link may never have held this funnel.
   */
  const finishConfirmation = useCallback(
    (confirmed: SignupStartedResponse) => {
      setSignupToken(confirmed.sessionToken);
      navigate('/signup/company');
    },
    [navigate],
  );

  /**
   * A link that no longer confirms has usually already been used — most often by this very browser, a
   * moment ago. Reading the mail twice must not strand the client on a screen asking for a code that was
   * spent along with the link, so the two ways on are tried before giving up:
   *
   * 1. Signed in here already — the whole funnel is past this screen, and `/signup` is what places them.
   * 2. Still holding the funnel session — the confirmation may have gone through in this browser or another
   *    one, and a funnel that is past it belongs on the company form rather than on a code screen.
   * 3. The same, for a funnel whose account is already raised: the login is the only way back into it.
   * 4. None of those — the mail was opened where the signup never was, and the screen says so rather than
   *    inviting a code that has nothing to be checked against.
   */
  const recoverFromSpentLink = useCallback(async() => {
    if (getAccessToken() != null) {
      // A page load rather than a `navigate`: the placing happens on startup, and a client-side hop would
      // land before it runs.
      window.location.href = SIGNUP_ROUTE;
      return;
    }

    if (getSignupToken() == null) return;

    try {
      const { data } = await api.getState();
      if (data.allowedActions.includes('save_company_info')) {
        navigate('/signup/company');
        return;
      }
      /*
        Past that form: the account exists, this browser holds nothing for it, and the funnel session stops
        standing in for a password minutes after the account is raised. Judged by the form being closed
        rather than by `companyCreated` alone — that flag is set part-way through provisioning, and a funnel
        stalled there belongs back on the form above.
      */
      if (data.companyCreated) {
        navigate('/login');
        return;
      }
    } catch {
      // No answer to act on. The notice below stands, and the code from the same email may still work.
    }

    setLinkNotice('This link is no longer valid — enter the code from the email instead.');
  }, [api, navigate]);

  // The link is the path most people take, so it confirms on arrival: nothing to type, nothing to press.
  const linkAttempted = useRef(false);

  const retryLink = useCallback(() => {
    linkAttempted.current = false;
    setLinkUnavailable(false);
    setConfirmingLink(true);
    setLinkAttempt(current => current + 1);
  }, []);

  useEffect(() => {
    if (linkToken == null || linkAttempted.current) return;
    linkAttempted.current = true;

    api
      .confirmLink(linkToken)
      .then(({ data }) => finishConfirmation(data))
      .catch((error: unknown) => {
        // A link is spent only when the API says so. Condemned on a timeout, it sends the client to the code
        // form — and a browser that opened the mail for the first time holds no funnel session, so the six
        // digits have nothing to be checked against and the notice above them rules out the one way back.
        if (isTransientFailure(error)) {
          setLinkUnavailable(true);
          return;
        }
        return recoverFromSpentLink();
      })
      .finally(() => setConfirmingLink(false));
  }, [api, finishConfirmation, linkAttempt, linkToken, recoverFromSpentLink]);

  const changeCode = useCallback((value: string) => {
    setCode(value);
    setError(null);
  }, []);

  const confirmCode = useCallback(async() => {
    try {
      await signupCodeValidationSchema.validate({ code: code });
    } catch (validationError) {
      setError((validationError as { message?: string }).message ?? 'The code is six digits.');
      return;
    }

    if (getSignupToken() == null) {
      setError(NO_SESSION_MESSAGE);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.confirmCode(code);
      finishConfirmation(data);
    } catch (e) {
      setError(errorMessage(e, 'The code could not be confirmed. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  }, [api, code, finishConfirmation]);

  const resendCode = useCallback(async() => {
    if (getSignupToken() == null) {
      setError(NO_SESSION_MESSAGE);
      return;
    }

    setResending(true);
    setError(null);
    try {
      await api.sendVerification();
      // The digits already typed are the ones the new email has just invalidated: the server keeps one code
      // per funnel and this request replaced it. Left in the field they get submitted and refused, and the
      // client reads that as the new code being wrong.
      setCode('');
      setLinkNotice('A new email is on its way. The previous link and code no longer work.');
    } catch (e) {
      setError(errorMessage(e, 'A new code could not be sent. Please try again in a minute.'));
    } finally {
      setResending(false);
    }
  }, [api]);

  /**
   * A live code goes out unasked, because the alternative is a screen inviting six digits that are certain to
   * be refused, with the way out — "Send a new code" — last on it. A refusal from the server lands in the
   * ordinary error line and leaves that button where it is.
   */
  const staleCodeReplaced = useRef(false);
  useEffect(() => {
    // Never on the link path: a fresh code would invalidate the very link being confirmed.
    if (!codeIsStale || linkToken != null || staleCodeReplaced.current) return;
    staleCodeReplaced.current = true;
    void resendCode();
  }, [codeIsStale, linkToken, resendCode]);

  // Both actions on this screen carry a session in a header, and this browser holds neither token that
  // could fill it. Derived rather than held: the link path writes both of them.
  const sessionMissing = getSignupToken() == null && getAccessToken() == null;

  const goToLogin = useCallback(() => navigate('/login'), [navigate]);
  const startOver = useCallback(() => navigate(SIGNUP_ROUTE), [navigate]);

  /**
   * The one thing this screen cannot fix by trying again: a code sent to the wrong address. It is the account
   * step that owns those details, and the funnel accepts them again for as long as nothing has been confirmed
   * — saving them there drops the code sent to the old address along the way.
   */
  const changeEmail = useCallback(() => navigate('/signup/account'), [navigate]);

  return {
    code,
    changeCode,
    error,
    linkNotice,
    confirmingLink,
    linkUnavailable,
    sessionMissing,
    retryLink,
    submitting,
    resending,
    confirmCode,
    resendCode,
    goToLogin,
    startOver,
    changeEmail,
  };
};
